import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Container,
  Box,
  IconButton,
  Typography,
  List,
  Divider,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItem,
  ListItemText,
  ListItemIcon,
  Toolbar
} from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Drawer } from "./UI/Drawer";
import { AppBar } from "./UI/AppBar";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountBoxOutlinedIcon from "@mui/icons-material/AccountBoxOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import { useTranslation } from "react-i18next";
import LinkIcon from '@mui/icons-material/Link';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';

// Іконки для акордеону
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BookIcon from '@mui/icons-material/Book';
import MonitorIcon from '@mui/icons-material/Monitor';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GroupIcon from '@mui/icons-material/Group';
import ShieldIcon from '@mui/icons-material/Shield';
import SettingsIcon from '@mui/icons-material/Settings';

export const MainWrapper = () => {
  const user = useSelector(state => state.users, shallowEqual);
  const showAllMenus = user.gotUserInfo && !user.user.user?.isApplicant;

  const [open, setOpen] = useState(window.innerWidth <= 768 ? false : true);
  const { t } = useTranslation();

  // Cookie Notification Logic
  const COOKIE_NAME = "hide_university_moodle_vote";
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };
  const setCookie = (name, value, days = 365) => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/`;
  };

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const deadline = new Date("2026-09-30T00:00:00");
    const hasClosed = getCookie(COOKIE_NAME) === "true";
    if (new Date() < deadline && !hasClosed) {
      setIsVisible(true);
    }
  }, []);

  const handleCloseNotification = () => {
    setIsVisible(false);
    setCookie(COOKIE_NAME, "true", 565);
  };

  const toggleDrawer = () => setOpen(!open);
  const location = useLocation();

  const MenuListItem = ({ linkTo, name, icon }) => (
    <ListItem
      selected={location.pathname === linkTo}
      component={Link}
      to={linkTo}
      button
      sx={{ "&.Mui-selected": { backgroundColor: "#F1FFF6" } }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={name} />
    </ListItem>
  );

  const theme = createTheme({
    status: { danger: "#fff" },
    components: {
      MuiListItem: { styleOverrides: { button: { color: "#212b36" } } },
      MuiListItemText: { styleOverrides: { primary: { fontSize: "14px" } } },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar position="absolute" open={open}>
          <Toolbar sx={{ pr: "24px", backgroundColor: "#00A43C" }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{ marginRight: "36px", marginLeft: "-9px", ...(open && { display: "none" }) }}
            >
              <MenuIcon />
            </IconButton>
            <Typography component="h1" variant="h6" noWrap sx={{ flexGrow: 1 }}>
              {t("mainWrapMenu.HeaderProfile")}
            </Typography>
          </Toolbar>
        </AppBar>

        <Drawer variant="permanent" open={open}>
          <Toolbar sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", px: [1] }}>
            <IconButton onClick={toggleDrawer}>
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider />
          <List component="nav" sx={{ pt: 0 }}>
            <MenuListItem linkTo={"/profile/info"} name={t("mainWrapMenu.Profile")} icon={<AccountBoxOutlinedIcon />} />
            {showAllMenus && (
              <>
                <MenuListItem linkTo={"/profile/requests"} name={t("mainWrapMenu.requests")} icon={<DashboardOutlinedIcon />} />
                <MenuListItem linkTo={"/profile/request/chat/new"} name={t("mainWrapMenu.create")} icon={<DashboardOutlinedIcon />} />
                <MenuListItem linkTo={"/profile/financial/study/reports"} name={t("mainWrapMenu.studyReport")} icon={<SchoolIcon />} />
                <MenuListItem linkTo={"/profile/financial/rent/reports"} name={t("mainWrapMenu.financialReport")} icon={<AccountBalanceIcon />} />
                <MenuListItem linkTo={"/profile/bypass/lists"} name={t("mainWrapMenu.CheckList")} icon={<FormatListBulletedIcon />} />
              </>
            )}
            <MenuListItem linkTo={"/profile/links"} name={t("mainWrapMenu.Links")} icon={<LinkIcon />} />
            <MenuListItem linkTo={"/profile/schedule"} name={t("mainWrapMenu.schedule")} icon={<ScheduleIcon />} />
            <MenuListItem linkTo={"/profile/library"} name={t("mainWrapMenu.library")} icon={<LocalLibraryIcon />} />
            <MenuListItem linkTo={"/logout/"} name={t("mainWrapMenu.Logout")} icon={<LogoutIcon />} />
          </List>
        </Drawer>

        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[900],
            flexGrow: 1,
            height: "100vh",
            overflow: "auto",
            minWidth: '250px'
          }}
        >
          <Toolbar />
          <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>

            {/* Повідомлення про анкетування */}
            {isVisible && (
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  "& .MuiAlert-icon": {color: "#0288d1"},
                }}
                action={
                  <IconButton
                    color="inherit"
                    size="small"
                    onClick={handleCloseNotification}
                  >
                    ✕
                  </IconButton>
                }
              >
                <AlertTitle sx={{fontWeight: 600}}>
                  Шановні здобувачі вищої освіти!
                </AlertTitle>
                Запрошуємо Вас взяти участь в анкетуванні щодо якості освітнього процесу в Сумському НАУ.

                <strong>Відкриті опитування:</strong>

                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>
                    <strong>Якість викладання дисциплін</strong> (весняний семестр 2025–2026) —
                    для 1 та 2 рівнів вищої освіти.
                  </li>
                  <li>
                    <strong>Якість освітніх програм</strong> —
                    для здобувачів 4 курсу бакалаврату.
                  </li>
                </ul>

                Ваші відповіді важливі для покращення якості освіти.
                Проходьте анкетування в особистому кабінеті <strong>Moodle</strong>.
                Анкетування анонімне.

                Дякуємо за активну участь!
              </Alert>
            )}

            {/* ІНФОРМАЦІЙНА ДОВІДКА */}
            <Accordion
              defaultExpanded={false}
              sx={{
                mb: 4,
                boxShadow: 3,
                borderRadius: 3,
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                sx={{
                  backgroundColor: '#166534',
                  color: 'white',
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h7" sx={{ fontWeight: 'bold' }}>
                    ІНФОРМАЦІЙНА ДОВІДКА
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 0, backgroundColor: '#f8fafc' }}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 3, color: '#166534', fontWeight: 600 }}>
                    План заходів за результатами опитування щодо якості викладання навчальних дисциплін<br />
                    в осінньому семестрі 2025–2026 н.р.
                  </Typography>

                  <Box sx={{ backgroundColor: '#ecfdf5', borderLeft: '5px solid #166534', p: 3, mb: 4, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#166534' }}>
                      Шановні здобувачі вищої освіти!
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      Дякуємо за участь в опитуванні. Ваші відповіді були узагальнені та розглянуті
                      на рівні університету. За результатами опитування сформовано план заходів
                      для вдосконалення освітнього процесу.
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {[
                      { num: 1, icon: <BookIcon />, title: "Навчально-методичні матеріали", text: "До початку 2026–2027 н.р., надалі – щосеместрово.<br />Оновлення літератури, методичних рекомендацій, презентацій і завдань для самостійної роботи." },
                      { num: 2, icon: <MonitorIcon />, title: "Moodle-курси", text: "До початку кожного семестру.<br />Упорядкування структури курсів: силабус, графік навчання, критерії оцінювання, матеріали, завдання, засоби комунікації." },
                      { num: 3, icon: <FactCheckIcon />, title: "Інформування про оцінювання", text: "На початку кожного семестру.<br />Ознайомлення з результатами навчання, критеріями оцінювання, строками виконання завдань, консультаціями, умовами перескладання та порядком апеляції." },
                      { num: 4, icon: <GroupIcon />, title: "Практична спрямованість навчання", text: "До початку наступного семестру.<br />Шире використання кейсів, дискусій, групових завдань, презентацій, аналізу практичних ситуацій і проектних робіт." },
                      { num: 5, icon: <ShieldIcon />, title: "Підтримка в умовах воєнного стану", text: "Постійно.<br />Розміщення в Moodle матеріалів для самостійного опрацювання; доступні формати для завантаження та перегляду офлайн." },
                      { num: 6, icon: <SchoolIcon />, title: "Академічна доброчесність", text: "Постійно.<br />Роз’яснення принципів академічної доброчесності під час виконання індивідуальних, курсових, кваліфікаційних і наукових робіт." },
                      { num: 7, icon: <SettingsIcon />, title: "Оновлення освітніх програм", text: "Щорічно.<br />Результати опитування враховуються під час перегляду освітніх програм, силабусів і освітніх компонентів." },
                    ].map((item) => (
                      <Box key={item.num} sx={{ display: 'flex', gap: 3 }}>
                        <Box sx={{
                          width: 45,
                          height: 45,
                          backgroundColor: '#166534',
                          color: 'white',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          {item.num}
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            {item.icon}
                            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>{item.title}</Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" dangerouslySetInnerHTML={{ __html: item.text }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                    <Box flex={1}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Виконання заходів забезпечують факультети СНАУ, кафедри, гаранти освітніх програм і викладачі.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Детальніше: розділ «Анкетування з питань забезпечення якості освіти» на сайті СНАУ.
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Скануйте QR-код</Typography>
                      <Box sx={{ p: 1.5, backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: 2 }}>
                        <img
                          src="https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=https://qrfy.io/u81K-YrW6b"
                          alt="QR Code"
                          width={140}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Outlet />
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};